import React from 'react';

import { CircleHelp, FileText, Sheet } from 'lucide-react';

import { OrgPermission } from '../../../../../../../../../../../backend/src/organisation/organisation-permission';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { KimMode } from '@/types';

interface ModeSelectorProps {
  mode: KimMode;
  setMode: (mode: KimMode) => void;
  setPlaceholder: (placeholder: string) => void;
}

export function ModeSelector({ mode, setMode, setPlaceholder }: ModeSelectorProps) {
  const { isEnabled } = useOrgPermissions();
  const isAnthropicModelsEnabled = isEnabled(OrgPermission.AnthropicModels);

  return (
    <div className="mt-2 grid w-full grid-cols-3 gap-4">
      <ModeOption
        mode={KimMode.QuestionFlow}
        currentMode={mode}
        icon={<CircleHelp className="h-4 w-4" />}
        title="Question"
        description="Ask a question and get an answer."
        onClick={() => {
          setMode(KimMode.QuestionFlow);
          setPlaceholder('Tell me about X...');
        }}
      />
      <ModeOption
        mode={KimMode.ExtractionFlow}
        currentMode={mode}
        icon={<Sheet className="h-4 w-4" />}
        title="Extraction"
        description="Extract information/entities into a report."
        onClick={() => {
          setMode(KimMode.ExtractionFlow);
          setPlaceholder('Extract all mentions of Y into a report...');
        }}
      />
      <ModeOption
        mode={KimMode.DraftFlow}
        currentMode={mode}
        icon={<FileText className="h-4 w-4" />}
        title={`Draft${isAnthropicModelsEnabled ? '' : ' (coming soon)'}`}
        disabled={!isAnthropicModelsEnabled}
        description="Create a draft document backed by facts."
        onClick={() => {
          setMode(KimMode.DraftFlow);
          setPlaceholder('Prepare me a draft letter of [X] about [Y]...');
        }}
      />
    </div>
  );
}

interface ModeOptionProps {
  mode: string;
  currentMode: KimMode;
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}

const ModeOption: React.FC<ModeOptionProps> = ({
  mode,
  currentMode,
  icon,
  title,
  description,
  onClick,
  disabled = false,
}) => {
  const isActive = mode === currentMode;
  const baseClasses = 'relative p-4 rounded-lg border transition-all duration-300 ease-in-out transform';
  const activeClasses = isActive ? 'border-blue-500 bg-blue-50 shadow-md scale-[1.02]' : 'border-gray-200 ';
  const hoverClasses = disabled ? '' : 'hover:shadow-lg hover:scale-[1.02]';
  const disabledClasses = disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer';

  return (
    <button
      className={`${baseClasses} ${activeClasses} ${hoverClasses} ${disabledClasses}`}
      onClick={onClick}
      disabled={disabled}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className={`transition-transform duration-300 ${isActive ? 'text-blue-500' : 'text-gray-600'}`}>
            {icon}
          </div>
          <h3
            className={`text-sm font-medium transition-colors duration-300 ${isActive ? 'text-blue-700' : 'text-gray-900'}`}
          >
            {title}
          </h3>
        </div>
        <p className="text-xs text-gray-600 transition-opacity duration-300">{description}</p>
      </div>
      {isActive && <div className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-blue-500" />}
    </button>
  );
};
